import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, Volume2 } from 'lucide-react';

const slideInUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOutDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled(motion.div)`
  position: absolute;
  bottom: 24px;
  right: 24px;
  left: 24px;
  z-index: 100;
  max-width: 400px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  backdrop-filter: blur(12px);
  
  @media (min-width: 768px) {
    right: 24px;
    left: auto;
    margin: 0;
  }
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
`;

const ToastTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark.text};
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ToastContent = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
`;

const ToastMessage = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.5;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const ToastActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ $primary, theme }) => 
    $primary 
      ? `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`
      : theme.colors.dark.elevated
  };
  border: 1px solid ${({ $primary, theme }) => 
    $primary ? 'transparent' : theme.colors.dark.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ $primary, theme }) => 
    $primary ? 'white' : theme.colors.dark.text
  };
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    ${({ $primary, theme }) => 
      $primary 
        ? `box-shadow: 0 8px 25px ${theme.colors.primary[500]}40;`
        : `background: ${theme.colors.dark.border};`
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const HighlightEffect = styled(motion.div)`
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: ${({ theme }) => theme.borderRadius.md};
  z-index: -1;
  opacity: 0;
`;

const FirstTimeToast = ({ isVisible, onClose, onOpenSettings }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    // Auto-dismiss after 8 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => clearTimeout(timer);
  }, [isVisible]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleSettingsClick = () => {
    setShowHighlight(true);
    onOpenSettings();
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <ToastContainer
        className={isClosing ? 'closing' : ''}
        initial={{ opacity: 0, y: 100, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.95 }}
        transition={{ duration: 0.4, type: "spring", damping: 25 }}
      >
        <ToastHeader>
          <ToastTitle>
            <Volume2 />
            Background Listening Active
          </ToastTitle>
          <CloseButton onClick={handleClose}>
            <X />
          </CloseButton>
        </ToastHeader>
        
        <ToastContent>
          <ToastMessage>
            SARVIK is now listening in the background. You can customize listening hours 
            or disable this feature in your voice settings.
          </ToastMessage>
        </ToastContent>
        
        <ToastActions>
          <ActionButton
            onClick={handleClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Got it
          </ActionButton>
          
          <ActionButton
            $primary
            onClick={handleSettingsClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Settings />
            App Settings
            <HighlightEffect
              animate={showHighlight ? { opacity: [0, 0.3, 0] } : {}}
              transition={{ duration: 2, repeat: 2 }}
            />
          </ActionButton>
        </ToastActions>
      </ToastContainer>
    </AnimatePresence>
  );
};

export default FirstTimeToast;